import { useEffect, useMemo, useRef, useState } from 'react';
import type { Anomaly, Mover, Scene } from '../../shared/scene';

interface SceneStageProps {
  scene: Scene;
  running: boolean;
  reveal?: boolean;
  onComplete?: () => void;
}

const VIEW_W = 100;
const VIEW_H = 70;

export function SceneStage({ scene, running, reveal = false, onComplete }: SceneStageProps) {
  const [elapsed, setElapsed] = useState(0);
  const startRef = useRef<number | null>(null);
  const completeRef = useRef(false);

  useEffect(() => {
    if (!running) {
      if (!reveal) setElapsed(0);
      startRef.current = null;
      completeRef.current = false;
      return;
    }
    let raf = 0;
    const tick = (now: number) => {
      if (startRef.current == null) startRef.current = now;
      const next = Math.min(scene.durationMs, now - startRef.current);
      setElapsed(next);
      if (next >= scene.durationMs) {
        if (!completeRef.current) {
          completeRef.current = true;
          onComplete?.();
        }
        return;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [running, reveal, scene.durationMs, onComplete]);

  const stars = useMemo(() => makeStars(scene.seed), [scene.seed]);
  const activeElapsed = reveal ? scene.anomaly.startMs + scene.anomaly.durationMs * 0.52 : elapsed;
  const progress = Math.round((elapsed / scene.durationMs) * 100);

  return (
    <div className="stageShell" aria-label="Animated counting scene">
      <div className="stageHud">
        <span>{scene.worldName}</span>
        <span>{running ? `${Math.max(0, Math.ceil((scene.durationMs - elapsed) / 1000))}s` : reveal ? 'Reveal' : 'Ready'}</span>
      </div>
      <svg className="stage" viewBox={`0 0 ${VIEW_W} ${VIEW_H}`} role="img" aria-describedby="scene-caption">
        <defs>
          <linearGradient id="voidGradient" x1="0" x2="1" y1="0" y2="1">
            <stop offset="0" stopColor="#111827" />
            <stop offset="0.55" stopColor="#0a0d14" />
            <stop offset="1" stopColor="#150d2b" />
          </linearGradient>
          <filter id="softGlow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="1.2" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
        <rect width={VIEW_W} height={VIEW_H} fill="url(#voidGradient)" />
        {stars.map((s) => (
          <circle key={s.id} cx={s.x} cy={s.y} r={s.r} fill={s.color} opacity={s.opacity} />
        ))}
        <g opacity="0.14">
          {Array.from({ length: 8 }, (_, i) => (
            <line key={i} x1={0} x2={VIEW_W} y1={8 + i * 8} y2={8 + i * 8} stroke="#f8fafc" strokeWidth="0.2" />
          ))}
          {Array.from({ length: 10 }, (_, i) => (
            <line key={i} x1={5 + i * 10} x2={5 + i * 10} y1={0} y2={VIEW_H} stroke="#f8fafc" strokeWidth="0.2" />
          ))}
        </g>
        <line x1={scene.gateX} x2={scene.gateX} y1="4" y2="66" stroke="#f6f1d5" strokeWidth="1.2" strokeDasharray="3 2" opacity="0.9" />
        <text x={scene.gateX + 1.5} y="7" className="svgTiny" fill="#f6f1d5">gate</text>
        <AnomalyGlyph anomaly={scene.anomaly} elapsed={activeElapsed} reveal={reveal} />
        {scene.movers.map((mover) => (
          <MoverGlyph key={mover.id} mover={mover} elapsed={activeElapsed} />
        ))}
        {reveal ? <RevealOverlay scene={scene} /> : null}
      </svg>
      <p id="scene-caption" className="srOnly">{scene.task.prompt}</p>
      <div className="progress" aria-hidden="true"><span style={{ width: `${progress}%` }} /></div>
    </div>
  );
}

function MoverGlyph({ mover, elapsed }: { mover: Mover; elapsed: number }) {
  const p = clamp((elapsed - mover.startMs) / mover.durationMs, 0, 1);
  const visible = elapsed >= mover.startMs && elapsed <= mover.startMs + mover.durationMs;
  if (!visible) return null;
  const x = lerp(mover.start.x, mover.end.x, easeInOut(p));
  const y = lerp(mover.start.y, mover.end.y, easeInOut(p));
  const opacity = Math.sin(p * Math.PI) * 0.9 + 0.1;
  const pulse = 1 + Math.sin((elapsed / 180) * mover.pulse) * 0.14;
  const size = mover.size * pulse;
  const common = { fill: mover.color, opacity, filter: mover.kind === 'target' ? 'url(#softGlow)' : undefined };
  return (
    <g transform={`translate(${x} ${y})`} className={mover.kind === 'target' ? 'targetGlyph' : ''}>
      {mover.shape === 'circle' && <circle r={size} {...common} />}
      {mover.shape === 'diamond' && <rect x={-size} y={-size} width={size * 2} height={size * 2} transform="rotate(45)" {...common} />}
      {mover.shape === 'square' && <rect x={-size} y={-size} width={size * 2} height={size * 2} rx="0.4" {...common} />}
      {mover.shape === 'triangle' && <path d={`M 0 ${-size * 1.25} L ${size * 1.2} ${size} L ${-size * 1.2} ${size} Z`} {...common} />}
      {mover.shape === 'dash' && <rect x={-size * 1.7} y={-size * 0.45} width={size * 3.4} height={size * 0.9} rx="1" {...common} />}
    </g>
  );
}

function AnomalyGlyph({ anomaly, elapsed, reveal }: { anomaly: Anomaly; elapsed: number; reveal: boolean }) {
  const p = clamp((elapsed - anomaly.startMs) / anomaly.durationMs, 0, 1);
  const visible = reveal || (elapsed >= anomaly.startMs && elapsed <= anomaly.startMs + anomaly.durationMs);
  if (!visible) return null;
  const x = lerp(anomaly.start.x, anomaly.end.x, easeInOut(p));
  const y = lerp(anomaly.start.y, anomaly.end.y, easeInOut(p));
  const opacity = reveal ? 1 : 0.82;
  const transform = `translate(${x} ${y}) rotate(${lerp(-3, 3, p)})`;
  return (
    <g transform={transform} opacity={opacity} className="anomalyGlyph">
      {renderAnomalyShape(anomaly)}
      {anomaly.type === 'text intrusion' || reveal ? (
        <text x="0" y={anomaly.size + 4} textAnchor="middle" fill="#f8fafc" className="svgLabel">{anomaly.label}</text>
      ) : null}
    </g>
  );
}

function renderAnomalyShape(anomaly: Anomaly) {
  const s = anomaly.size;
  const stroke = '#f8fafc';
  switch (anomaly.shape) {
    case 'gorilla':
      return <g><ellipse rx={s * 0.8} ry={s * 0.9} fill={anomaly.color} stroke={stroke} strokeWidth="0.7"/><circle cx={-s * 0.42} cy={-s * 0.62} r={s * 0.22} fill={anomaly.color}/><circle cx={s * 0.42} cy={-s * 0.62} r={s * 0.22} fill={anomaly.color}/><circle cx={-s * 0.25} cy={-s * 0.08} r={s * 0.08} fill="#0a0d14"/><circle cx={s * 0.25} cy={-s * 0.08} r={s * 0.08} fill="#0a0d14"/></g>;
    case 'whale':
      return <g><ellipse rx={s * 1.25} ry={s * 0.55} fill={anomaly.color} stroke={stroke} strokeWidth="0.7"/><path d={`M ${s * 1.05} 0 L ${s * 1.65} ${-s * 0.45} L ${s * 1.55} ${s * 0.45} Z`} fill={anomaly.color}/><circle cx={-s * 0.55} cy={-s * 0.12} r={s * 0.07} fill="#0a0d14"/></g>;
    case 'door':
      return <g><rect x={-s * 0.55} y={-s} width={s * 1.1} height={s * 1.8} rx="1" fill={anomaly.color} stroke={stroke} strokeWidth="0.7"/><circle cx={s * 0.25} cy="0" r={s * 0.07} fill="#0a0d14"/><path d={`M ${-s * 0.35} ${s * 0.92} q ${-s * 0.35} ${s * 0.5} ${-s * 0.85} 0`} stroke={stroke} fill="none"/><path d={`M ${s * 0.35} ${s * 0.92} q ${s * 0.35} ${s * 0.5} ${s * 0.85} 0`} stroke={stroke} fill="none"/></g>;
    case 'moon':
      return <g><circle r={s * 0.72} fill={anomaly.color} stroke={stroke} strokeWidth="0.7"/><circle cx={s * 0.3} cy={-s * 0.1} r={s * 0.62} fill="#0a0d14" opacity="0.7"/></g>;
    case 'banner':
      return <g><rect x={-s * 1.9} y={-s * 0.55} width={s * 3.8} height={s * 1.1} rx="2" fill={anomaly.color} stroke={stroke} strokeWidth="0.7"/><text x="0" y="1.8" textAnchor="middle" fill="#0a0d14" className="svgBanner">LOOK</text></g>;
    case 'eye':
      return <g><path d={`M ${-s * 1.2} 0 Q 0 ${-s} ${s * 1.2} 0 Q 0 ${s} ${-s * 1.2} 0 Z`} fill={anomaly.color} stroke={stroke} strokeWidth="0.7"/><circle r={s * 0.36} fill="#0a0d14"/><circle r={s * 0.16} fill="#f6f1d5"/></g>;
    case 'umbrella':
      return <g><path d={`M ${-s} 0 A ${s} ${s} 0 0 1 ${s} 0 L ${-s} 0`} fill={anomaly.color} stroke={stroke} strokeWidth="0.7"/><path d={`M 0 0 L 0 ${s * 1.1} q 0 ${s * 0.4} ${s * 0.45} ${s * 0.15}`} stroke={stroke} strokeWidth="0.7" fill="none"/></g>;
    default:
      return <circle r={s} fill={anomaly.color} />;
  }
}

function RevealOverlay({ scene }: { scene: Scene }) {
  const a = scene.anomaly;
  return (
    <g pointerEvents="none">
      <line x1={a.start.x} y1={a.start.y} x2={a.end.x} y2={a.end.y} stroke="#f6f1d5" strokeWidth="1.1" strokeDasharray="2 1" opacity="0.9" />
      <circle cx={a.start.x} cy={a.start.y} r="2" fill="#f6f1d5" />
      <circle cx={a.end.x} cy={a.end.y} r="2" fill="#f6f1d5" />
      <text x="4" y="66" fill="#f6f1d5" className="svgTiny">Unexpected: {a.label} · {a.type}</text>
    </g>
  );
}

function makeStars(seed: string) {
  let h = 0;
  for (const c of seed) h = Math.imul(31, h) + c.charCodeAt(0);
  const rand = () => {
    h ^= h << 13;
    h ^= h >> 17;
    h ^= h << 5;
    return ((h >>> 0) % 10000) / 10000;
  };
  return Array.from({ length: 90 }, (_, id) => ({
    id,
    x: rand() * VIEW_W,
    y: rand() * VIEW_H,
    r: rand() * 0.28 + 0.05,
    opacity: rand() * 0.45 + 0.12,
    color: rand() > 0.9 ? '#ffcf5a' : '#f8fafc'
  }));
}

function lerp(a: number, b: number, p: number) {
  return a + (b - a) * p;
}

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

function easeInOut(t: number) {
  return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
}
